import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import OpenPositionsSection from './OpenPositionsSection';
import MultiAssetTradePanel, { useTradePanelScroll } from './MultiAssetTradePanel';
import OmniMapActiveBatchSection from './OmniMapActiveBatchSection';
import { positionKey } from '../utils/tradeActionUi';
import { fmtUsd } from '../utils/fmtPrice';
import { pickActiveEvmWallet, connectEvmWallet } from '../utils/detectEvmWallets';
import { EVM_WALLET_INSTALL_MSG } from '../utils/evmWalletMessages';
import { tryHlSignNetworkSwitch, isChainMismatchError } from '../utils/hlSignNetwork';
import './OBotTrading.css';
import './OpenPositionsSection.css';
import './MultiAssetTradePanel.css';
import './AdminMultiAssetBotSettings.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

const OMNI_API = {
  positions: `${API_BASE}/multi-asset/admin/omni/positions`,
  liquidateAll: `${API_BASE}/multi-asset/admin/omni/liquidate-all`,
  liquidate: `${API_BASE}/multi-asset/admin/omni/liquidate`,
  signals: `${API_BASE}/multi-asset/admin/omni/signals`,
  confirm: (id) => `${API_BASE}/multi-asset/admin/omni/signals/${id}/confirm`,
  paperBalance: `${API_BASE}/multi-asset/admin/omni/paper-balance`,
  mode: `${API_BASE}/multi-asset/admin/omni/mode`,
  wallet: `${API_BASE}/multi-asset/admin/omni/wallet`,
  agentPrepare: `${API_BASE}/multi-asset/admin/omni/agent/prepare`,
  agentConfirm: `${API_BASE}/multi-asset/admin/omni/agent/confirm`,
  seedBaseline: `${API_BASE}/multi-asset/admin/omni/seed-baseline`,
};

const defaultForm = {
  symbol: 'GOLD',
  hlDex: 'xyz',
  hlCoin: null,
  leverage: 2,
  hlMaxLeverage: null,
  side: 'long',
  action: 'open',
  sizeUsd: 5000,
  stopPrice: '',
  targetPrice: '',
  reason: '',
};

export default function AdminOmniMapTrading() {
  const { panelRef, scrollToPanel } = useTradePanelScroll();
  const [allowed, setAllowed] = useState(null);
  const [config, setConfig] = useState(null);
  const [omniStatus, setOmniStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [closeQuickBusy, setCloseQuickBusy] = useState(false);
  const [positionsRefresh, setPositionsRefresh] = useState(0);
  const [paperBalance, setPaperBalance] = useState('100000');
  const [savingBalance, setSavingBalance] = useState(false);
  const [pendingQuote, setPendingQuote] = useState(null);
  const [walletBusy, setWalletBusy] = useState(false);
  const [modeBusy, setModeBusy] = useState(false);
  const [walletInput, setWalletInput] = useState('');
  const [omniWallet, setOmniWallet] = useState(null);
  const [baselineJson, setBaselineJson] = useState('');
  const [baselineBusy, setBaselineBusy] = useState(false);

  const setStatus = (text, isError = false) => {
    if (isError) setError(text);
    else setMessage(text);
    if (isError) setMessage(null);
    else setError(null);
  };

  const load = useCallback(async () => {
    setError(null);
    try {
      const [cfg, omni, wallet] = await Promise.all([
        axios.get(`${API_BASE}/multi-asset/config`),
        axios.get(OMNI_API.positions, { headers: authHeaders() }),
        axios.get(OMNI_API.wallet, { headers: authHeaders() }).catch(() => ({ data: null })),
      ]);
      setConfig(cfg.data);
      setOmniStatus(omni.data);
      setOmniWallet(wallet.data);
      if (wallet.data?.walletAddress) setWalletInput(wallet.data.walletAddress);
      if (omni.data.paperTestBalanceUsd != null) {
        setPaperBalance(String(omni.data.paperTestBalanceUsd));
      }
      const syms = cfg.data.allowedSymbols?.length
        ? cfg.data.allowedSymbols
        : cfg.data.knownSymbols || [];
      if (syms.length && !syms.includes(form.symbol)) {
        setForm((f) => ({ ...f, symbol: syms[0] }));
      }
    } catch (e) {
      if (e.response?.status === 403) setAllowed(false);
      else setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [form.symbol]);

  useEffect(() => {
    axios
      .get(`${API_BASE}/multi-asset/admin/settings`, { headers: authHeaders() })
      .then(() => setAllowed(true))
      .catch(() => setAllowed(false));
  }, []);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load, positionsRefresh]);

  useEffect(() => {
    const sym = form.symbol;
    const dex = form.hlDex || 'xyz';
    if (!sym) return;
    axios
      .get(`${API_BASE}/multi-asset/market-meta/${sym}`, {
        headers: authHeaders(),
        params: { dex },
      })
      .then(({ data }) => {
        setForm((f) => ({
          ...f,
          hlMaxLeverage: data.maxLeverage ?? null,
          hlDex: data.dex || f.hlDex,
          hlCoin: data.coin || f.hlCoin,
        }));
      })
      .catch(() => {});
  }, [form.symbol, form.hlDex]);

  if (allowed === false) return <Navigate to="/dashboard" replace />;
  if (loading && allowed == null) return <div className="obot-page loading">Checking admin access…</div>;
  if (loading) return <div className="obot-page loading">Loading Omni MAP trading…</div>;

  const paper = omniStatus?.paperMode !== false;
  const symbols = config?.allowedSymbols?.length
    ? config.allowedSymbols
    : config?.knownSymbols || [form.symbol];
  const strategyLev = config?.strategyLeverage ?? 2;

  const applyPositionToForm = (p) => {
    setForm({
      ...defaultForm,
      symbol: p.symbol,
      leverage: p.leverage || 2,
      side: p.closeSide,
      action: 'close',
      sizeUsd: p.marginUsd ?? p.sizeUsd ?? 5000,
      reason: `Admin close ${p.side} ${p.symbol}`,
    });
  };

  const handleSelectPosition = (p) => {
    setSelectedPosition(p);
    applyPositionToForm(p);
    scrollToPanel();
  };

  const handleTradeLot = (p) => {
    setSelectedPosition(p);
    applyPositionToForm(p);
    scrollToPanel();
    setStatus(
      `Partial close on Omni ${p.symbol} — adjust margin below (MAP strategy uses ${strategyLev}x).`
    );
  };

  const handleCloseLot = async (p) => {
    const defaultPx =
      p.symbol === 'TSLA' && Number(p.markPrice) > 0 && Number(p.markPrice) < 320
        ? '330'
        : p.markPrice != null
          ? String(p.markPrice)
          : '';
    const raw = window.prompt(
      `Close Omni ${p.side} ${p.symbol} at what price?\n` +
        `(Leave blank to use current mark. For the 2026-07-23 TSLA reconcile use 330.)`,
      defaultPx
    );
    if (raw === null) return; // cancelled
    const closeAtPrice = String(raw).trim() === '' ? undefined : Number(raw);
    if (closeAtPrice != null && !(closeAtPrice > 0)) {
      setStatus('Close cancelled — enter a valid price or leave blank for mark.', true);
      return;
    }
    setCloseQuickBusy(true);
    try {
      const body = { symbol: p.symbol, side: p.side, lotKey: p.lotKey };
      if (closeAtPrice != null) body.closeAtPrice = closeAtPrice;
      const { data } = await axios.post(OMNI_API.liquidate, body, { headers: authHeaders() });
      setStatus(data.message || `Closed ${p.symbol}`);
      setPositionsRefresh((k) => k + 1);
    } catch (e) {
      setStatus(e.response?.data?.error || e.message, true);
    } finally {
      setCloseQuickBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setMessage(null);
    try {
      const payload = {
        symbol: form.symbol,
        side: form.side,
        action: form.action,
        sizeUsd: Number(form.sizeUsd),
        leverage: Number(form.leverage) || 2,
        hlDex: form.hlDex,
        hlCoin: form.hlCoin,
        reason: form.reason || 'Admin trade — Omni MAP account',
      };
      if (form.action === 'close' && selectedPosition?.sizeContracts) {
        payload.payload = { closeContractsExact: Number(selectedPosition.sizeContracts) };
      }
      const { data } = await axios.post(OMNI_API.signals, payload, { headers: authHeaders() });
      if (data.requiresQuoteConfirm) {
        setPendingQuote(data);
        return;
      }
      setStatus(data.message || `Omni trade #${data.signalId} ${data.filled ? 'filled' : 'queued'}.`);
      setPositionsRefresh((k) => k + 1);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmPaperQuote = async () => {
    if (!pendingQuote?.signalId) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await axios.post(OMNI_API.confirm(pendingQuote.signalId), {}, { headers: authHeaders() });
      setStatus(data.message || 'Omni paper fill confirmed.');
      setPendingQuote(null);
      setPositionsRefresh((k) => k + 1);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const savePaperBalance = async () => {
    setSavingBalance(true);
    try {
      const { data } = await axios.patch(
        OMNI_API.paperBalance,
        { omniPaperTestBalanceUsd: Math.max(0, Number(paperBalance) || 0) },
        { headers: authHeaders() }
      );
      setStatus(`Omni paper test balance saved: ${fmtUsd(data.omniPaperTestBalanceUsd)}`);
      setPositionsRefresh((k) => k + 1);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSavingBalance(false);
    }
  };

  const togglePaperMode = async (nextPaper) => {
    setModeBusy(true);
    try {
      await axios.patch(OMNI_API.mode, { paperMode: nextPaper }, { headers: authHeaders() });
      setStatus(`Omni MAP mode set to ${nextPaper ? 'PAPER' : 'LIVE'}.`);
      setPositionsRefresh((k) => k + 1);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setModeBusy(false);
    }
  };

  const importMapBaseline = async (replaceBaseline = false) => {
    let parsed;
    try {
      parsed = JSON.parse(baselineJson.trim());
    } catch {
      setError('Invalid JSON — paste portfolioSummary object or full omni-map-active.json');
      return;
    }
    const portfolioSummary = parsed.portfolioSummary || parsed;
    if (!portfolioSummary?.positions?.length) {
      setError('JSON must include portfolioSummary.positions[] (or positions[] at root)');
      return;
    }
    setBaselineBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(
        OMNI_API.seedBaseline,
        { portfolioSummary, replaceBaseline },
        { headers: authHeaders() }
      );
      setStatus(data.message || `Seeded ${data.seeded} position(s).`);
      setBaselineJson('');
      setPositionsRefresh((k) => k + 1);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBaselineBusy(false);
    }
  };

  const connectBrowserWallet = async () => {
    setWalletBusy(true);
    try {
      const w = await pickActiveEvmWallet();
      if (!w) throw new Error(EVM_WALLET_INSTALL_MSG);
      const { address } = await connectEvmWallet(w.provider);
      const { data } = await axios.put(OMNI_API.wallet, { walletAddress: address }, { headers: authHeaders() });
      setOmniWallet(data);
      setWalletInput(data.walletAddress || address);
      setStatus(`Omni HL wallet linked: ${address.slice(0, 6)}…${address.slice(-4)}`);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setWalletBusy(false);
    }
  };

  const saveWalletAddress = async () => {
    setWalletBusy(true);
    try {
      const { data } = await axios.put(
        OMNI_API.wallet,
        { walletAddress: walletInput.trim() },
        { headers: authHeaders() }
      );
      setOmniWallet(data);
      setStatus('Omni HL wallet address saved.');
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setWalletBusy(false);
    }
  };

  const approveOmniAgent = async () => {
    setWalletBusy(true);
    try {
      const axiosLib = (await import('axios')).default;
      const { signUserSignedAction } = await import('@nktkas/hyperliquid/signing');
      const { BrowserProvider } = await import('ethers');
      const w = await pickActiveEvmWallet();
      if (!w) throw new Error(EVM_WALLET_INSTALL_MSG);
      const { address } = await connectEvmWallet(w.provider);
      const walletAddr = omniWallet?.walletAddress || walletInput;
      if (walletAddr && address.toLowerCase() !== walletAddr.toLowerCase()) {
        throw new Error('Connected wallet does not match the linked Omni HL wallet.');
      }
      const { data: prepared } = await axiosLib.post(
        OMNI_API.agentPrepare,
        {},
        { headers: authHeaders() }
      );
      const browserProvider = new BrowserProvider(w.provider);
      const signer = await browserProvider.getSigner(address);
      let signature;
      try {
        signature = await signUserSignedAction({
          wallet: signer,
          action: prepared.action,
          types: {
            'HyperliquidTransaction:ApproveAgent': [
              { name: 'hyperliquidChain', type: 'string' },
              { name: 'agentAddress', type: 'address' },
              { name: 'agentName', type: 'string' },
              { name: 'nonce', type: 'uint64' },
            ],
          },
        });
      } catch (signErr) {
        if (isChainMismatchError(signErr)) {
          await tryHlSignNetworkSwitch(w.provider);
          signature = await signUserSignedAction({
            wallet: signer,
            action: prepared.action,
            types: {
              'HyperliquidTransaction:ApproveAgent': [
                { name: 'hyperliquidChain', type: 'string' },
                { name: 'agentAddress', type: 'address' },
                { name: 'agentName', type: 'string' },
                { name: 'nonce', type: 'uint64' },
              ],
            },
          });
        } else {
          throw signErr;
        }
      }
      const { data } = await axiosLib.post(
        OMNI_API.agentConfirm,
        { action: prepared.action, nonce: prepared.nonce, signature },
        { headers: authHeaders() }
      );
      setOmniWallet(data);
      setStatus(data.message || 'Omni agent approved.');
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setWalletBusy(false);
    }
  };

  return (
    <div className="obot-page admin-omni-map-page">
      <header className="obot-header">
        <p>
          <Link to="/dashboard">← Dashboard</Link>
          {' · '}
          <Link to="/admin">Admin</Link>
        </p>
        <h1>Omni MAP — master account trading</h1>
        <p className="subtitle">
          Admin-only control of the Omni MAP master book. Trades here also broadcast MAP signals to every
          enabled oBot and the Manual Trades signal feed (paper or live).
        </p>
      </header>

      {error && <div className="obot-error">{error}</div>}
      {message && <div className="obot-success">{message}</div>}

      <section className="obot-card admin-omni-settings">
        <h2>Settings</h2>
        <div className="admin-omni-settings-grid">
          <div className="admin-omni-setting-block">
            <span className="label">Trading mode</span>
            <div className="obot-leverage-row">
              <span className={`pill ${paper ? 'mode-paper' : 'mode-live'}`}>{paper ? 'PAPER' : 'LIVE'}</span>
              <button
                type="button"
                className="secondary-btn"
                disabled={modeBusy}
                onClick={() => togglePaperMode(!paper)}
              >
                {modeBusy ? 'Saving…' : paper ? 'Switch to live' : 'Switch to paper'}
              </button>
            </div>
            <p className="hint">Global MAP mode — same as Admin → Multi-asset oBot settings.</p>
          </div>

          <div className="admin-omni-setting-block">
            <span className="label">Omni Hyperliquid wallet</span>
            <div className="obot-leverage-row">
              <input
                type="text"
                placeholder="0x…"
                value={walletInput}
                onChange={(e) => setWalletInput(e.target.value)}
              />
              <button type="button" className="secondary-btn" disabled={walletBusy} onClick={saveWalletAddress}>
                Save
              </button>
              <button type="button" className="secondary-btn" disabled={walletBusy} onClick={connectBrowserWallet}>
                Connect wallet
              </button>
            </div>
            {omniWallet?.agentApproved ? (
              <p className="hint ma-admin-success-inline">Agent approved ✓ — live trades use EIP-712 (no server private key).</p>
            ) : (
              <p className="hint">Live mode: link wallet, then approve the trade-only agent (one signature).</p>
            )}
            {!paper && omniWallet?.walletAddress && !omniWallet?.agentApproved && (
              <button type="button" className="primary-btn" disabled={walletBusy} onClick={approveOmniAgent}>
                {walletBusy ? 'Signing…' : 'Approve Omni agent (EIP-712)'}
              </button>
            )}
          </div>
        </div>

        {paper && (
          <div className="admin-omni-setting-block">
            <span className="label">Paper test balance</span>
            <div className="obot-leverage-row">
              <input
                type="number"
                min={0}
                step={1000}
                value={paperBalance}
                onChange={(e) => setPaperBalance(e.target.value)}
              />
              <button type="button" className="secondary-btn" disabled={savingBalance} onClick={savePaperBalance}>
                {savingBalance ? 'Saving…' : 'Save balance'}
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="obot-card admin-omni-settings">
        <h2>Import MAP baseline / add missing lots</h2>
        <p className="hint">
          Paste a <code>portfolioSummary</code> (or full inbox JSON).{' '}
          <strong>Import / add missing</strong> creates only lots that are not already on the Omni Master
          ledger (safe for fixing one missing name like MSFT).{' '}
          <strong>Replace &amp; import</strong> wipes all Omni paper lots first — use only for a full rebuild.
        </p>
        <textarea
          className="ma-inbox-prompt-pre"
          rows={6}
          placeholder='Paste { "portfolioSummary": { "positions": [...] } } or full inbox JSON'
          value={baselineJson}
          onChange={(e) => setBaselineJson(e.target.value)}
          disabled={baselineBusy}
        />
        <div className="obot-leverage-row" style={{ marginTop: 12 }}>
          <button
            type="button"
            className="primary-btn"
            disabled={baselineBusy || !baselineJson.trim()}
            onClick={() => importMapBaseline(false)}
          >
            {baselineBusy ? 'Importing…' : 'Import / add missing'}
          </button>
          <button
            type="button"
            className="secondary-btn"
            disabled={baselineBusy || !baselineJson.trim()}
            onClick={() => {
              if (window.confirm('Replace all existing Omni paper lots with this baseline?')) {
                importMapBaseline(true);
              }
            }}
          >
            Replace &amp; import
          </button>
        </div>
      </section>

      <OmniMapActiveBatchSection refreshKey={positionsRefresh} />

      <OpenPositionsSection
        selectedPositionKey={selectedPosition ? positionKey(selectedPosition) : null}
        onSelectPosition={handleSelectPosition}
        onTradeLot={handleTradeLot}
        onCloseLot={handleCloseLot}
        onStatusMessage={setStatus}
        onPositionsChanged={() => setPositionsRefresh((k) => k + 1)}
        refreshKey={positionsRefresh}
        scope="omni"
        title="Omni MAP open positions"
        apiPaths={{
          positions: OMNI_API.positions,
          liquidateAll: OMNI_API.liquidateAll,
        }}
      />

      <MultiAssetTradePanel
        panelRef={panelRef}
        form={form}
        setForm={setForm}
        symbols={symbols}
        submitting={submitting}
        onSubmit={submit}
        selectedPosition={selectedPosition}
        onClosePositionQuick={handleCloseLot}
        closeQuickBusy={closeQuickBusy}
        tradingMode="omni"
        leverageCap={config?.obotMaxLeverage ?? 2}
        hlMaxLeverage={form.hlMaxLeverage}
        strategyLeverage={strategyLev}
        paperMode={paper}
        submitLabel={paper ? 'Queue paper trade' : 'Execute live trade'}
      />

      {pendingQuote && (
        <div className="wallet-gear-overlay" role="presentation" onClick={() => !submitting && setPendingQuote(null)}>
          <div
            className="wallet-gear-dialog"
            role="dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Confirm Omni paper trade</h2>
            <p>
              {pendingQuote.symbol} · {pendingQuote.side} · margin {fmtUsd(pendingQuote.deploy)} ·{' '}
              {pendingQuote.leverage}x
            </p>
            {error && <div className="obot-error">{error}</div>}
            <div className="obot-leverage-row">
              <button type="button" className="secondary-btn" disabled={submitting} onClick={() => setPendingQuote(null)}>
                Cancel
              </button>
              <button type="button" className="primary-btn" disabled={submitting} onClick={confirmPaperQuote}>
                {submitting ? 'Filling…' : 'Confirm fill'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
